import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  createRoutesFromChildren,
  matchRoutes,
  useNavigationType,
} from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import { ToastContainer } from "react-toastify";
import "./assets/css/react-toastify.css";

import { ThemeProvider, useThemeMode } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { usePermissions } from "./hooks/usePermissions";
import { ProjectProvider } from "./contexts/ProjectContext";
import { OrganizationProvider } from "./contexts/OrganizationContext";
import AppRefreshProvider from "./components/AppRefreshProvider";
import { ConfidentialNavigationGuard } from "./components/confidential-navigation-guard";
import "./theme/designSystemVariables.css";

// Pages
import Login from "./pages/Login";
import LogoutCallback from "./pages/LogoutCallback";
import MoyasarPaymentCallback from "./pages/MoyasarPaymentCallback";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Deployments from "./pages/Deployments";
import DeploymentDetail from "./pages/DeploymentDetail";
import CreateDeployment from "./pages/CreateDeployment";
import PredefinedModels from "./pages/PredefinedModels";
import Flows from "./pages/Flows";
import TemplateDetail from "./pages/TemplateDetail";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import UsageDashboard from "./pages/UsageDashboardNew/index";
import UsageAndCost from "./pages/UsageAndCost/index";
import DocumentCollectionsPage from "./pages/DocumentCollectionsPage";
import CollectionDetailPage from "./pages/CollectionDetailPage";
import ChatPlayground from "./pages/ChatPlayground";
import ChatStandalone from "./pages/ChatStandalone";
import AICatalog from "./pages/AICatalog";
import NewAICatalog from "./pages/AICatalog/index";
import ProjectsManagement from "./pages/ProjectsManagement";
import ApiKeys from "./pages/ApiKeys";
import MCPs from "./pages/MCPs";
import { Playground } from "./pages/Playground/Playground";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ThemePage from "./pages/Theme/index";
import {
  createReactRouterV6Options,
  getWebInstrumentations,
  initializeFaro,
  ReactIntegration,
  ReactRouterVersion,
  WebVitalsInstrumentation,
} from "@grafana/faro-react";
import { FaroRoutes } from "@grafana/faro-react";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";

import "./i18n";

import i18n from "i18next";
import {
  applyLanguageToDocument,
  getPersistedLanguage,
} from "./utils/language";
import Blueprints from "./pages/Blueprints";

applyLanguageToDocument(
  i18n.resolvedLanguage || i18n.language || getPersistedLanguage(),
);

// Silent SSO check page for Keycloak
const SilentCheckSSO = () => null;

// ✅ Protected route
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  if (loading || currentUser === undefined) {
    return <div>{i18n.t("common.loading")}</div>;
  }

  if (!currentUser) {
    return <Login state={{ from: location }} />;
  }

  return children;
};

const PermissionRoute = ({ allowed, children }) =>
  allowed ? children : <Navigate to="/" replace />;

const DeploymentRoute = ({ children }) => {
  const { canViewDeployments } = usePermissions();
  return (
    <PermissionRoute allowed={canViewDeployments}>{children}</PermissionRoute>
  );
};

const ApiKeyRoute = ({ children }) => {
  const { canViewApiKey } = usePermissions();
  return <PermissionRoute allowed={canViewApiKey}>{children}</PermissionRoute>;
};

const HideInferenceOnlyRoute = ({ children }) => {
  const { isInferenceOnly } = usePermissions();
  return (
    <PermissionRoute allowed={!isInferenceOnly}>{children}</PermissionRoute>
  );
};

// Custom close button for toast
const CloseButton = ({ closeToast }) => (
  <button
    onClick={closeToast}
    style={{
      position: "relative",
      background: "transparent",
      outline: "none",
      border: "none",
      padding: 0,
      cursor: "pointer",
      opacity: 0.7,
      transition: "0.3s ease",
      alignSelf: "flex-start",
      color: "inherit",
    }}
    aria-label={i18n.t("common.close")}
  >
    <svg
      aria-hidden="true"
      viewBox="0 0 14 16"
      width="14"
      height="16"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M7.71 8.23l3.75 3.75-1.48 1.48-3.75-3.75-3.75 3.75-1.48-1.48 3.75-3.75L0.77 4.48l1.48-1.48 3.75 3.75 3.75-3.75 1.48 1.48-3.75 3.75z"
      />
    </svg>
  </button>
);

// Toast container with theme support
const ThemedToastContainer = () => {
  const { mode } = useThemeMode();

  const [isRTL, setIsRTL] = React.useState(i18n.dir() === "rtl");

  React.useEffect(() => {
    const handleLanguageChange = (language) => {
      const normalizedLanguage = applyLanguageToDocument(language);
      setIsRTL(normalizedLanguage === "ar");
    };

    i18n.on("languageChanged", handleLanguageChange);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, []);

  return (
    <ToastContainer
      key={`${i18n.language}-${isRTL}`}
      position="bottom-right"
      theme={mode}
      closeButton={CloseButton}
      autoClose={5000}
      pauseOnHover
      hideProgressBar={false}
      newestOnTop={false}
      rtl={isRTL}
    />
  );
};

const observabilityEnabled =
  window.env?.OBSERVABILITY_ENABLED === "true" ||
  window.env?.OBSERVABILITY_ENABLED === true;

if (observabilityEnabled) {
  const collectorEndpoint = window.env?.FARO_COLLECTOR_ENDPOINT;
  if (!collectorEndpoint) {
    console.warn("Faro enabled but FARO_COLLECTOR_ENDPOINT is missing.");
  } else {
    const url = collectorEndpoint.endsWith("/collect")
      ? collectorEndpoint
      : `${collectorEndpoint}/collect`;

    initializeFaro({
      url,
      app: {
        name: window.env?.FARO_APP_NAME,
        version: window.env?.FARO_APP_VERSION,
        environment: window.env?.FARO_APP_ENVIRONMENT,
      },
      apiKey: window.env?.FARO_API_KEY,
      instrumentations: [
        // Load the default Web instrumentations
        ...getWebInstrumentations(),
        new TracingInstrumentation(),
        new ReactIntegration({
          router: createReactRouterV6Options({
            createRoutesFromChildren,
            matchRoutes,
            Routes,
            useLocation,
            useNavigationType,
          }),
        }),
        new WebVitalsInstrumentation(),
      ],
      webVitalsInstrumentation: {
        reportAllChanges: true,
      },
      sessionTracking: {
        samplingRate: Number(window.env?.FARO_SAMPLING_RATE || 1),
      },
    });
  }
}
const RoutesWrapper = observabilityEnabled ? FaroRoutes : Routes;

const queryClient = new QueryClient();
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <OrganizationProvider>
          <ProjectProvider>
            <ThemeProvider>
              <CssBaseline />
              <Router>
                <AppRefreshProvider>
                  <ConfidentialNavigationGuard />
                  <RoutesWrapper>
                    {/* Public routes */}
                    <Route path="/login" element={<Login />} />
                    <Route
                      path="/logout-callback"
                      element={<LogoutCallback />}
                    />
                    <Route
                      path="/payment/moyasar/callback"
                      element={<MoyasarPaymentCallback />}
                    />
                    <Route path="/register" element={<Register />} />
                    <Route
                      path="/silent-check-sso.html"
                      element={<SilentCheckSSO />}
                    />

                    {/* Protected routes */}
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/deployments"
                      element={
                        <ProtectedRoute>
                          <DeploymentRoute>
                            <Deployments />
                          </DeploymentRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/deployments/:id"
                      element={
                        <ProtectedRoute>
                          <DeploymentRoute>
                            <DeploymentDetail />
                          </DeploymentRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/create-deployment"
                      element={
                        <ProtectedRoute>
                          <DeploymentRoute>
                            <CreateDeployment />
                          </DeploymentRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/predefined-models"
                      element={
                        <ProtectedRoute>
                          <DeploymentRoute>
                            <PredefinedModels />
                          </DeploymentRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <Settings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/usage"
                      element={
                        <ProtectedRoute>
                          <UsageAndCost />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/update/theme/:organization_id"
                      element={
                        <ProtectedRoute>
                          <HideInferenceOnlyRoute>
                            <ThemePage />
                          </HideInferenceOnlyRoute>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/collections"
                      element={
                        <ProtectedRoute>
                          <HideInferenceOnlyRoute>
                            <DocumentCollectionsPage />
                          </HideInferenceOnlyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/collections/:collectionId"
                      element={
                        <ProtectedRoute>
                          <HideInferenceOnlyRoute>
                            <CollectionDetailPage />
                          </HideInferenceOnlyRoute>
                        </ProtectedRoute>
                      }
                    />
                    {/* Legacy Playground routes are intentionally disabled so
                    users cannot open the old Playground through a direct URL. */}
                    {false && (
                      <>
                        <Route
                          path="/legacyplayground"
                          element={
                            <ProtectedRoute>
                              <ChatPlayground />
                              {/* <Playground />*/}
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/legacyplayground-standalone"
                          element={
                            <ProtectedRoute>
                              <ChatStandalone />
                              {/* <CollectionDetailPage /> */}

                              {/* <Playground singleRenderedChildren />*/}
                            </ProtectedRoute>
                          }
                        />
                      </>
                    )}
                    <Route
                      path="/playground/:id?"
                      element={
                        <ProtectedRoute>
                          {/* <ChatPlayground /> */}
                          <Playground />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/chat-standalone/:id?"
                      element={
                        <ProtectedRoute>
                          {/* <ChatStandalone /> */}
                          {/* <CollectionDetailPage /> */}

                          <Playground singleRenderedChildren />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/ai-catalog"
                      element={
                        <ProtectedRoute>
                          <NewAICatalog />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/old-ai-catalog"
                      element={
                        <ProtectedRoute>
                          <AICatalog />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/api-keys"
                      element={
                        <ProtectedRoute>
                          <ApiKeyRoute>
                            <Navigate
                              to="/platform/security/api-keys"
                              replace
                            />
                          </ApiKeyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/platform/security/api-keys"
                      element={
                        <ProtectedRoute>
                          <ApiKeyRoute>
                            <ApiKeys />
                          </ApiKeyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/mcps"
                      element={
                        <ProtectedRoute>
                          <HideInferenceOnlyRoute>
                            <MCPs />
                          </HideInferenceOnlyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/bluePrints"
                      element={
                        <ProtectedRoute>
                          <HideInferenceOnlyRoute>
                            <Blueprints />
                          </HideInferenceOnlyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/projects"
                      element={
                        <ProtectedRoute>
                          <ProjectsManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/flows"
                      element={
                        <ProtectedRoute>
                          <HideInferenceOnlyRoute>
                            <Flows />
                          </HideInferenceOnlyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/flows/:ProjectId/:flowId"
                      element={
                        <ProtectedRoute>
                          <HideInferenceOnlyRoute>
                            <Flows />
                          </HideInferenceOnlyRoute>
                        </ProtectedRoute>
                      }
                    />
                    {/* Registered as its own route + component (not folded
                        into Flows' :ProjectId/:flowId branching) - react-router
                        v6 ranks this literal "explore" segment above the
                        sibling route's dynamic :ProjectId, so the two never
                        collide. */}
                    <Route
                      path="/flows/explore/:templateId"
                      element={
                        <ProtectedRoute>
                          <HideInferenceOnlyRoute>
                            <TemplateDetail />
                          </HideInferenceOnlyRoute>
                        </ProtectedRoute>
                      }
                    />

                    {/* Debug/test route */}
                    <Route
                      path="/test"
                      element={<div>Test Route Works!</div>}
                    />

                    {/* Fallback 404 */}
                    <Route path="*" element={<NotFound />} />
                  </RoutesWrapper>
                </AppRefreshProvider>
              </Router>
              <ThemedToastContainer />
            </ThemeProvider>
          </ProjectProvider>
        </OrganizationProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
